import { getCurrentPlayer } from '../helpers';
import { RPC } from '@mixer/postmessage-rpc';
import { markStoryStart } from '../helpers/player';

const FORWARDED_EVENTS = ['sw.story.shown', 'sw.story.start', 'sw.story.end', 'sw.variant.start', 'sw.variant.progress', 'sw.choice.selected', 'sw.advancedlogic.updateContext'];

const FORWARDED_VIDEOJS_EVENTS = ['play', 'pause'];

export default function setupEmbed(players, aLContext) {
  // Add a message event listener for sw.button.click to click button with specific data-id
  window.addEventListener('message', (event) => {
    if (event.data.type === 'sw.button.click') {
      const buttonDataId = event.data.buttonDataId;
      console.info('Button ID passed in to click: ' + buttonDataId);

      let button = document.querySelector(`button[data-id="${buttonDataId}"]`);

      if (button) {
        button.click();
        console.info('Button successfully clicked: ' + buttonDataId);
      } else {
        console.error('Button not found: ' + buttonDataId);
      }
    }
  });

  // Extend the existing message event listener to add support for variable change events
  window.addEventListener('message', (event) => {
    switch (event.data.type) {
      // handle sw.variables.set, sw.variables.addup, and sw.variables.change messages containing behaviour data
      case 'sw.variables.set':
        aLContext.processSetBehaviours(event.data.behaviours);
        break;
      case 'sw.variables.addup':
        aLContext.processAddUpBehaviours(event.data.behaviours);
        break;
      case 'sw.variables.change':
        aLContext.processChangeBehaviours(event.data.behaviours);
        break;
    }
  });

  const endpoint = new RPC({
    target: window.parent,
    serviceId: window.location.toString(),
  });

  Object.entries(playerAPI(players, aLContext, dispatchEventToEndpoint)).forEach(([methodName, fn]) => {
    endpoint.expose(methodName, fn);
  });

  // Handshake endpoints to notify the host that we're ready
  // First directly notify the host that we're ready
  endpoint.call('ready');
  // But it may not be ready itself to receive that message
  // so we provide an endpoint to let it notify us that it is
  endpoint.expose('ping', () => {
    endpoint.call('ping');
  });

  function dispatchEventToEndpoint({ type, detail }) {
    return endpoint.call('dispatchEvent', {
      type,
      detail,
    });
  }
}

function playerAPI(players, aLContext, dispatchEvent) {
  // It's likely the `story.shown` event will happen ahead
  // of the iframe's host registering to get events forwarded
  // so we'll keep it warm in this variable and dispatch it once
  // the code in the iframe's host gets hooked
  let storyShownEvent;
  document.addEventListener(
    'sw.story.shown',
    once((event) => (storyShownEvent = event)),
  );

  // The event `sw.advancedlogic.updateContext` will be triggered
  // before the iframe is ready to receive messages so we'll keep
  // it warm in this variable and dispatch it once the code in the
  // iframe's host gets hooked
  let updateContextEvent;
  document.addEventListener(
    'sw.advancedlogic.updateContext',
    once((event) => (updateContextEvent = event)),
  );

  return {
    forwardEvents() {
      FORWARDED_EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, dispatchEvent);
      });

      FORWARDED_VIDEOJS_EVENTS.forEach((eventName) => {
        Object.values(players).forEach((player) => {
          player.on(eventName, (event) => {
            dispatchEvent(APIEventForVideoJSEvent(event));
          });
        });
      });

      // Forward the `story.shown` event if it already happened
      if (storyShownEvent) {
        dispatchEvent(storyShownEvent);
      }

      // Forward the `sw.advancedlogic.updateContext` event if it already happened
      if (updateContextEvent) {
        dispatchEvent(updateContextEvent);
      }
    },
    getStory() {
      return window.wrpVariants;
    },
    play() {
      const currentPlayer = getCurrentPlayer(Object.values(players));
      // Only call `play()` if we're actually playing a video
      // to avoid launching a video on an image island, which
      // would cause the next media loaded on the player to get
      // started as soon as set on the player rather than when players
      // get swapped
      if (currentPlayer.isVideo) {
        currentPlayer.play();
      } else {
        markStoryStart(currentPlayer);
      }
    },
    pause: forwardToCurrentPlayer(players, 'pause'),
    paused: forwardToCurrentPlayer(players, 'paused'),
    currentTime: forwardToCurrentPlayer(players, 'currentTime'),
    getCurrentContext() {
      return aLContext.currentContext;
    },
    getCurrentVar(varName) {
      return aLContext.currentContext[varName];
    },
  };
}

function APIEventForVideoJSEvent({ type }) {
  return {
    type: `sw.video.${type}`,
  };
}

function forwardToCurrentPlayer(players, method) {
  return function () {
    const player = getCurrentPlayer(Object.values(players));
    return player[method].apply(player, arguments);
  };
}

function once(fn) {
  return function (event) {
    event.target.removeEventListener(event.type, fn);
    return fn(event);
  };
}
